/**
 * WebXR Session Management
 *
 * Manages WebXR sessions with Three.js for immersive reading:
 * - World-locked reading pane (anchored in reference space, not attached to camera)
 * - Pane geometry from CSS tokens (0.65m × 0.22m at 1.2m distance)
 * - Canvas texture updates only when word changes
 * - "Enter XR" button integration
 *
 * Constitution Article XII compliance:
 * - Pane is world-locked always
 * - Button label is exactly "Enter XR"
 */

import * as THREE from 'three';
import { TextCanvas, createTextCanvas } from './textCanvas';
import { HintsCanvas, createHintsCanvas } from './xrHintsCanvas';
import { XRInputHandler, createXRInputHandler } from './xrInput';
import { XRSceneControls, createXRSceneControls } from './xrSceneControls';
import { XRPaneControls, createXRPaneControls } from './xrPaneControls';
import type { ReaderCore } from '../../core/readerCore';

/**
 * XR configuration from CSS tokens
 */
interface XRConfig {
  paneWidth: number;  // meters
  paneHeight: number; // meters
  paneDistance: number; // meters
  clearColor: string;
  clearAlpha: number;
  paneFill: string;
  paneBorder: string;
  // Environment tokens
  roomBg: string;
  roomGrid: string;
  objectMuted: string;
}

/**
 * Mobile device profile for XR positioning
 */
type MobileProfile = 'small' | 'standard' | 'large';

/**
 * Mobile device info for positioning calculations
 */
interface MobileDeviceInfo {
  profile: MobileProfile;
  screenHeight: number;
  screenWidth: number;
  isLandscape: boolean;
  safeAreaTop: number;
  safeAreaBottom: number;
}

/**
 * Default XR configuration (matches tokens.css)
 */
const DEFAULT_XR_CONFIG: XRConfig = {
  paneWidth: 0.65,
  paneHeight: 0.22,
  paneDistance: 1.2,
  clearColor: '#000000',
  clearAlpha: 1, // VR mode needs alpha=1 to show background
  paneFill: 'rgba(255, 255, 255, 0.1)',
  paneBorder: 'rgba(255, 255, 255, 0.2)',
  // Environment defaults
  roomBg: '#1e2127',
  roomGrid: 'rgba(255, 255, 255, 0.12)',
  objectMuted: 'rgba(255, 255, 255, 0.55)',
};

/**
 * Read CSS token as number
 */
function getCSSNumber(name: string, fallback: number): number {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!value) return fallback;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? fallback : parsed;
}

/**
 * Read CSS token as string
 */
function getCSSString(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

/**
 * Load XR configuration from CSS tokens
 */
function loadXRConfig(): XRConfig {
  return {
    paneWidth: getCSSNumber('--xr-reading-pane-width-m', DEFAULT_XR_CONFIG.paneWidth),
    paneHeight: getCSSNumber('--xr-reading-pane-height-m', DEFAULT_XR_CONFIG.paneHeight),
    paneDistance: getCSSNumber('--xr-reading-pane-distance-m', DEFAULT_XR_CONFIG.paneDistance),
    clearColor: getCSSString('--xr-clear-color', DEFAULT_XR_CONFIG.clearColor),
    clearAlpha: 1, // Always 1 for VR mode (not AR passthrough)
    paneFill: getCSSString('--xr-ui-panel-fill', DEFAULT_XR_CONFIG.paneFill),
    paneBorder: getCSSString('--xr-ui-panel-border', DEFAULT_XR_CONFIG.paneBorder),
    // Environment tokens
    roomBg: getCSSString('--xr-room-bg', DEFAULT_XR_CONFIG.roomBg),
    roomGrid: getCSSString('--xr-room-grid', DEFAULT_XR_CONFIG.roomGrid),
    objectMuted: getCSSString('--xr-object-muted', DEFAULT_XR_CONFIG.objectMuted),
  };
}

/**
 * XR Session Manager
 */
export class XRSessionManager {
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private textCanvas: TextCanvas | null = null;
  private textTexture: THREE.CanvasTexture | null = null;
  private readingPane: THREE.Mesh | null = null;
  private hintsCanvas: HintsCanvas | null = null;
  private hintsTexture: THREE.CanvasTexture | null = null;
  private hintsPane: THREE.Mesh | null = null;
  private xrSession: XRSession | null = null;
  private config: XRConfig;
  private unsubscribeCore: (() => void) | null = null;
  private inputHandler: XRInputHandler | null = null;
  private sceneControls: XRSceneControls | null = null;
  private paneControls: XRPaneControls | null = null;
  private currentWord: string = '';
  private isActive: boolean = false;
  private isPreviewMode: boolean = false;
  private isGyroMode: boolean = false;
  private deviceOrientation: { alpha: number; beta: number; gamma: number } = { alpha: 0, beta: 0, gamma: 0 };
  private initialOrientation: { alpha: number; beta: number; gamma: number } | null = null;
  private boundHandleOrientation: ((e: DeviceOrientationEvent) => void) | null = null;
  private environmentGroup: THREE.Group | null = null;
  // Camera passthrough for mobile AR mode
  private isCameraMode: boolean = false;
  private cameraStream: MediaStream | null = null;
  private cameraVideoElement: HTMLVideoElement | null = null;

  // Zoom prevention state
  private originalViewportContent: string | null = null;
  private originalBodyStyles: { touchAction: string; overflow: string; position: string; width: string; height: string } | null = null;

  // Orientation change handling
  private boundHandleOrientationChange: (() => void) | null = null;
  private mobileDeviceInfo: MobileDeviceInfo | null = null;

  // Window resize handling (desktop)
  private boundHandleResize: (() => void) | null = null;

  // Mobile 3D mode: starting tilt to show floor/objects below panel
  private mobileStartTiltDeg: number = 0;

  // Store 3D mode camera settings to restore after camera mode
  private saved3DCameraPosition: THREE.Vector3 | null = null;
  private saved3DCameraRotation: THREE.Euler | null = null;
  private saved3DTiltDeg: number = 0;

  // Store 3D mode pane settings to restore after camera mode
  private saved3DPanePosition: THREE.Vector3 | null = null;
  private saved3DPaneRotation: THREE.Euler | null = null;
  private saved3DPaneScale: THREE.Vector3 | null = null;

  constructor() {
    this.config = loadXRConfig();
  }

  /**
   * Check if WebXR is supported
   */
  static async isSupported(): Promise<boolean> {
    if (typeof navigator === 'undefined') {
      console.warn('[XR] Navigator undefined');
      return false;
    }
    if (!navigator.xr) {
      console.warn('[XR] WebXR API not available (navigator.xr is undefined). Enable WebXR emulator in Chrome DevTools.');
      return false;
    }
    try {
      const supported = await navigator.xr.isSessionSupported('immersive-vr');
      console.log(`[XR] immersive-vr session supported: ${supported}`);
      return supported;
    } catch (error) {
      console.error('[XR] Error checking session support:', error);
      return false;
    }
  }

  /**
   * Initialize Three.js scene for XR
   */
  private initScene(): void {
    // Create renderer with XR support
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.xr.enabled = true;

    // Set clear color from tokens (alpha=1 for VR)
    const clearColor = new THREE.Color(this.config.clearColor);
    this.renderer.setClearColor(clearColor, this.config.clearAlpha);

    // Create scene
    this.scene = new THREE.Scene();

    // Set scene background color (for VR mode)
    this.scene.background = new THREE.Color(this.config.roomBg);

    // Add fog for atmospheric depth cues
    this.scene.fog = new THREE.Fog(this.config.roomBg, 2, 15);

    // Create camera (will be controlled by XR)
    this.camera = new THREE.PerspectiveCamera(
      70,
      window.innerWidth / window.innerHeight,
      0.01,
      100
    );
    this.camera.position.set(0, 1.6, 0); // Eye height

    // Build environment (grid floor + ambient shapes)
    this.buildEnvironment();

    // Create text canvas for word rendering
    this.textCanvas = createTextCanvas();

    // Create texture from text canvas
    const canvas = this.textCanvas.getCanvas();
    this.textTexture = new THREE.CanvasTexture(canvas as HTMLCanvasElement);
    this.textTexture.minFilter = THREE.LinearFilter;
    this.textTexture.magFilter = THREE.LinearFilter;

    // Create reading pane with frosted glass effect
    this.createReadingPane();

    // Create hints panel showing keyboard shortcuts
    this.createHintsPane();

    // Add lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    // Add directional light for subtle shading on shapes
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.4);
    dirLight.position.set(5, 10, 5);
    this.scene.add(dirLight);
  }

  /**
   * Build the XR environment (grid floor + ambient geometric shapes)
   * Matches reference: XR-demo-room-reading-panel.png
   * Environment is placed in a group for gyro mode rotation.
   */
  private buildEnvironment(): void {
    if (!this.scene) return;

    // Create environment group (for gyro mode rotation)
    this.environmentGroup = new THREE.Group();
    this.scene.add(this.environmentGroup);

    // --- Grid Floor ---
    const floorY = 0;
    const gridSize = 20;
    const gridDivisions = 40;
    const gridHelper = new THREE.GridHelper(
      gridSize,
      gridDivisions,
      new THREE.Color(this.config.roomGrid),
      new THREE.Color(this.config.roomGrid)
    );
    gridHelper.position.y = floorY;
    // Make grid lines slightly transparent
    (gridHelper.material as THREE.Material).opacity = 0.5;
    (gridHelper.material as THREE.Material).transparent = true;
    this.environmentGroup.add(gridHelper);

    // --- Ambient Geometric Shapes (muted, not distracting) ---
    const mutedColor = new THREE.Color(this.config.objectMuted);
    const shapeMaterial = new THREE.MeshStandardMaterial({
      color: mutedColor,
      roughness: 0.8,
      metalness: 0.1,
    });

    // Cube on the left
    const cubeGeo = new THREE.BoxGeometry(0.5, 0.5, 0.5);
    const cube = new THREE.Mesh(cubeGeo, shapeMaterial.clone());
    cube.position.set(-1.8, floorY + 0.25, -2.5);
    this.environmentGroup.add(cube);

    // Small sphere on the right foreground
    const sphereGeo = new THREE.SphereGeometry(0.15, 32, 32);
    const sphere = new THREE.Mesh(sphereGeo, shapeMaterial.clone());
    sphere.position.set(1.5, floorY + 0.15, -1.8);
    this.environmentGroup.add(sphere);

    // Icosahedron on the right background
    const icoGeo = new THREE.IcosahedronGeometry(0.4, 0);
    const icosahedron = new THREE.Mesh(icoGeo, shapeMaterial.clone());
    icosahedron.position.set(2.2, floorY + 0.5, -3.5);
    this.environmentGroup.add(icosahedron);

    // Additional shapes for richer environment

    // Tall cylinder on far left
    const cylinderGeo = new THREE.CylinderGeometry(0.2, 0.2, 1.2, 32);
    const cylinder = new THREE.Mesh(cylinderGeo, shapeMaterial.clone());
    cylinder.position.set(-3.0, floorY + 0.6, -4.0);
    this.environmentGroup.add(cylinder);

    // Torus on left foreground
    const torusGeo = new THREE.TorusGeometry(0.25, 0.08, 16, 32);
    const torus = new THREE.Mesh(torusGeo, shapeMaterial.clone());
    torus.position.set(-2.0, floorY + 0.3, -1.5);
    torus.rotation.x = Math.PI / 4;
    this.environmentGroup.add(torus);

    // Octahedron floating higher on right
    const octaGeo = new THREE.OctahedronGeometry(0.3, 0);
    const octahedron = new THREE.Mesh(octaGeo, shapeMaterial.clone());
    octahedron.position.set(2.8, floorY + 1.2, -2.8);
    this.environmentGroup.add(octahedron);

    // Small cone near center-right
    const coneGeo = new THREE.ConeGeometry(0.18, 0.4, 32);
    const cone = new THREE.Mesh(coneGeo, shapeMaterial.clone());
    cone.position.set(0.8, floorY + 0.2, -3.2);
    this.environmentGroup.add(cone);

    // Dodecahedron far back center
    const dodecaGeo = new THREE.DodecahedronGeometry(0.35, 0);
    const dodecahedron = new THREE.Mesh(dodecaGeo, shapeMaterial.clone());
    dodecahedron.position.set(0, floorY + 0.4, -5.0);
    this.environmentGroup.add(dodecahedron);

    // Flat box (platform) on left background
    const platformGeo = new THREE.BoxGeometry(0.8, 0.1, 0.8);
    const platform = new THREE.Mesh(platformGeo, shapeMaterial.clone());
    platform.position.set(-2.5, floorY + 0.05, -4.5);
    this.environmentGroup.add(platform);
  }

  /**
   * Create the reading pane with frosted glass appearance
   */
  private createReadingPane(): void {
    if (!this.scene || !this.textTexture) return;

    // Create reading pane geometry
    const geometry = new THREE.PlaneGeometry(
      this.config.paneWidth,
      this.config.paneHeight
    );

    // Frosted glass material: blend text texture with semi-transparent background
    const material = new THREE.MeshBasicMaterial({
      map: this.textTexture,
      transparent: true,
      side: THREE.DoubleSide,
      opacity: 0.95,
    });

    // Create reading pane mesh
    this.readingPane = new THREE.Mesh(geometry, material);

    // Position pane in world space (world-locked)
    // Positioned directly in front at configured distance, at eye height
    this.readingPane.position.set(0, 1.6, -this.config.paneDistance);

    // Add to scene (NOT to camera - this makes it world-locked)
    this.scene.add(this.readingPane);

    // Add subtle border frame around the pane
    this.addPaneBorder();
  }

  /**
   * Add a subtle border frame around the reading pane
   */
  private addPaneBorder(): void {
    if (!this.scene || !this.readingPane) return;

    const borderWidth = 0.008; // 8mm border
    const w = this.config.paneWidth;
    const h = this.config.paneHeight;

    // Parse border color from token
    const borderMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.2,
    });

    // Create border edges as thin boxes
    const edges = [
      // Top edge
      { geo: new THREE.BoxGeometry(w + borderWidth * 2, borderWidth, 0.002), pos: [0, h / 2 + borderWidth / 2, 0] },
      // Bottom edge
      { geo: new THREE.BoxGeometry(w + borderWidth * 2, borderWidth, 0.002), pos: [0, -h / 2 - borderWidth / 2, 0] },
      // Left edge
      { geo: new THREE.BoxGeometry(borderWidth, h, 0.002), pos: [-w / 2 - borderWidth / 2, 0, 0] },
      // Right edge
      { geo: new THREE.BoxGeometry(borderWidth, h, 0.002), pos: [w / 2 + borderWidth / 2, 0, 0] },
    ];

    edges.forEach(({ geo, pos }) => {
      const edge = new THREE.Mesh(geo, borderMaterial);
      edge.position.set(pos[0], pos[1], pos[2]);
      this.readingPane!.add(edge); // Add as child so it moves with pane
    });
  }

  /**
   * Create the hints panel showing keyboard shortcuts
   * Positioned below and to the right of the reading pane
   */
  private createHintsPane(): void {
    if (!this.scene) return;

    // Create hints canvas
    this.hintsCanvas = createHintsCanvas();

    // Create texture from hints canvas
    const canvas = this.hintsCanvas.getCanvas();
    this.hintsTexture = new THREE.CanvasTexture(canvas as HTMLCanvasElement);
    this.hintsTexture.minFilter = THREE.LinearFilter;
    this.hintsTexture.magFilter = THREE.LinearFilter;

    // Hints pane size (smaller than reading pane)
    const hintsWidth = 0.32; // 32cm wide
    const hintsHeight = 0.24; // 24cm tall

    // Create geometry
    const geometry = new THREE.PlaneGeometry(hintsWidth, hintsHeight);

    // Material with texture
    const material = new THREE.MeshBasicMaterial({
      map: this.hintsTexture,
      transparent: true,
      side: THREE.DoubleSide,
      opacity: 0.9,
    });

    // Create mesh
    this.hintsPane = new THREE.Mesh(geometry, material);

    // Position: below and to the right of reading pane, not overlapping
    // Reading pane is 0.65m wide centered at x=0, so right edge is at x=0.325m
    // Hints pane is 0.32m wide, place with clear gap from reading pane
    const readingPaneY = 1.6;
    const hintsX = 0.55; // 55cm to the right (clear gap from reading pane)
    const hintsY = readingPaneY - 0.25; // 25cm below eye level
    const hintsZ = -this.config.paneDistance; // Same plane as reading pane

    this.hintsPane.position.set(hintsX, hintsY, hintsZ);

    // Keep parallel with reading pane (no rotation)

    // Add to scene (world-locked like reading pane)
    this.scene.add(this.hintsPane);
  }

  /**
   * Update the reading pane texture with a new word
   */
  private updateWord(word: string): void {
    if (!this.textCanvas || !this.textTexture) return;

    // Only update if word changed (optimization per Constitution IX)
    if (word === this.currentWord) return;
    this.currentWord = word;

    // Render word to canvas
    const updated = this.textCanvas.renderWord(word);

    // Update texture only if canvas changed
    if (updated) {
      this.textTexture.needsUpdate = true;
    }
  }

  /**
   * Subscribe to ReaderCore events
   */
  private subscribeToCore(core: ReaderCore): void {
    // Update with current word immediately
    this.updateWord(core.currentWord);

    // Subscribe to word changes
    this.unsubscribeCore = core.on((event) => {
      if (event.type === 'wordChange') {
        this.updateWord(event.word);
      }
    });
  }

  /**
   * Start XR session
   */
  async startSession(core: ReaderCore, container: HTMLElement): Promise<boolean> {
    if (this.isActive) return true;

    // Check WebXR support
    const supported = await XRSessionManager.isSupported();
    if (!supported) {
      console.warn('WebXR not supported');
      return false;
    }

    try {
      console.log('[XR] Initializing scene...');
      // Initialize scene
      this.initScene();

      if (!this.renderer) {
        throw new Error('Renderer not initialized');
      }

      // Append renderer to container
      container.appendChild(this.renderer.domElement);
      this.renderer.domElement.style.position = 'fixed';
      this.renderer.domElement.style.top = '0';
      this.renderer.domElement.style.left = '0';
      this.renderer.domElement.style.width = '100%';
      this.renderer.domElement.style.height = '100%';
      this.renderer.domElement.style.zIndex = '1000';

      // Disable page zoom for XR mode
      this.disablePageZoom();

      // Request XR session
      console.log('[XR] Requesting immersive-vr session...');
      this.xrSession = await navigator.xr!.requestSession('immersive-vr', {
        optionalFeatures: ['local-floor', 'bounded-floor'],
      });
      console.log('[XR] Session acquired successfully');

      // Set up session
      await this.renderer.xr.setSession(this.xrSession);

      // Subscribe to core for word updates
      this.subscribeToCore(core);

      // Create and start input handler
      this.inputHandler = createXRInputHandler(core);
      this.inputHandler.start();

      // Set up render loop with input processing
      this.renderer.setAnimationLoop((_time, frame) => this.render(frame));

      // Handle session end
      this.xrSession.addEventListener('end', () => {
        this.endSession();
      });

      this.isActive = true;
      return true;
    } catch (error) {
      console.error('Failed to start XR session:', error);
      this.cleanup();
      return false;
    }
  }

  /**
   * Start preview mode (non-XR 3D view)
   * - Mobile: Gyro mode - pane is head-locked, world moves with phone orientation
   * - Desktop: Touch/mouse controls - pane is world-locked, camera orbits
   */
  async startPreview(core: ReaderCore, container: HTMLElement): Promise<boolean> {
    if (this.isActive) return true;

    try {
      // Initialize scene
      this.initScene();

      if (!this.renderer || !this.camera) {
        throw new Error('Renderer or camera not initialized');
      }

      // Append renderer to container
      container.appendChild(this.renderer.domElement);
      this.renderer.domElement.style.position = 'fixed';
      this.renderer.domElement.style.top = '0';
      this.renderer.domElement.style.left = '0';
      this.renderer.domElement.style.width = '100%';
      this.renderer.domElement.style.height = '100%';
      this.renderer.domElement.style.zIndex = '1000';

      // Disable XR for preview mode
      this.renderer.xr.enabled = false;

      // Subscribe to core for word updates
      this.subscribeToCore(core);

      // Detect mobile and use gyro mode for AR-like experience
      const isMobile = XRSessionManager.isMobileDevice();
      const hasGyro = XRSessionManager.hasDeviceOrientation();
      console.log(`[XR] Device detection: mobile=${isMobile}, hasGyro=${hasGyro}`);

      if (isMobile && hasGyro) {
        // Mobile: Try gyro mode - pane stays fixed in view, world rotates
        console.log('[XR] Mobile detected, attempting gyro mode');
        const gyroStarted = await this.startGyroMode();

        if (gyroStarted && this.readingPane && this.scene && this.camera) {
          // Disable page zoom for mobile XR
          this.disablePageZoom();

          // Detect device profile for positioning
          this.mobileDeviceInfo = this.detectMobileProfile();

          // Calculate device-aware pane position
          const mobileDistance = getCSSNumber('--xr-mobile-pane-distance-m', 0.8);
          const mobileY = this.calculateMobilePaneY(this.mobileDeviceInfo);

          this.readingPane.position.set(0, mobileY, -mobileDistance);
          // Tilt panel backward (top away) so floor horizon rises into view
          // and panel appears more upright from camera's perspective
          this.readingPane.rotation.x = 0.18; // ~10 degrees backward tilt
          console.log(`[XR] Mobile pane positioned: Y=${mobileY}, Z=-${mobileDistance}, profile=${this.mobileDeviceInfo.profile}`);

          // Start camera below reading pane, looking up at scene
          // Floor is below (Y=0), pane is at mobileY
          this.camera.position.set(0, mobileY - 0.5, 1.0);  // Below pane, 1m back
          // No initial tilt - gyro handles view direction
          this.mobileStartTiltDeg = 0;

          // Start orientation change listener for repositioning on rotate
          this.startOrientationChangeListener();

          // Position hints pane relative to mobile reading pane (not overlapping)
          if (this.hintsPane) {
            this.hintsPane.position.set(0.55, mobileY - 0.25, -mobileDistance); // Same plane as reading pane
            this.hintsPane.visible = true;
          }
          console.log('[XR] Gyro mode active - gyro controls camera, touch controls pane');

          // Mobile 3D mode: enable pane controls (touch moves pane, not camera)
          // Gyro controls camera view, touch adjusts pane position/size/rotation
          // Pass camera for camera-relative panning
          this.paneControls = createXRPaneControls(
            this.readingPane,
            this.renderer.domElement,
            {},
            this.hintsPane,
            this.camera
          );
          this.paneControls.enable();
        } else {
          console.log('[XR] Gyro mode failed to start, falling back to touch controls');
        }
      }

      // Desktop only: enable scene controls (orbit camera around scene)
      // On mobile with gyro, camera is controlled by gyros and pane by touch
      if (!isMobile || !this.isGyroMode) {
        console.log('[XR] Desktop mode - enabling camera orbit controls');
        this.sceneControls = createXRSceneControls(this.camera, this.renderer.domElement, {
          target: new THREE.Vector3(0, 1.6, -this.config.paneDistance),
        });
        this.sceneControls.enable();
      }

      // Set up render loop (no XR frame)
      this.isPreviewMode = true;
      this.renderer.setAnimationLoop(() => this.render());

      // Start resize listener for desktop window resizing
      this.startResizeListener();

      this.isActive = true;
      return true;
    } catch (error) {
      console.error('Failed to start preview mode:', error);
      this.cleanup();
      return false;
    }
  }

  /**
   * End preview mode
   */
  endPreview(): void {
    if (!this.isPreviewMode) return;
    this.stopGyroMode();
    this.cleanup();
    this.isActive = false;
    this.isPreviewMode = false;
    this.isGyroMode = false;
  }

  /**
   * Check if device orientation is available (mobile gyro)
   */
  private static hasDeviceOrientation(): boolean {
    return typeof DeviceOrientationEvent !== 'undefined' &&
           typeof (DeviceOrientationEvent as any).requestPermission === 'function' ||
           'DeviceOrientationEvent' in window;
  }

  /**
   * Check if this is likely a mobile device
   */
  private static isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (navigator.maxTouchPoints > 0 && window.matchMedia('(max-width: 767px)').matches);
  }

  /**
   * Detect mobile device profile based on screen dimensions
   * Returns device category for positioning calculations
   */
  private detectMobileProfile(): MobileDeviceInfo {
    const screenHeight = window.innerHeight;
    const screenWidth = window.innerWidth;
    const isLandscape = screenWidth > screenHeight;

    // Determine device profile based on screen height (portrait mode reference)
    // These thresholds are based on common device dimensions
    const effectiveHeight = isLandscape ? screenWidth : screenHeight;
    let profile: MobileProfile;

    if (effectiveHeight >= 900) {
      // Pro Max (932px), large Android phones
      profile = 'large';
    } else if (effectiveHeight >= 780) {
      // Pro (852px), standard Android (800px)
      profile = 'standard';
    } else {
      // SE, compact phones (< 780px)
      profile = 'small';
    }

    // Get safe area insets (for notch/Dynamic Island)
    const safeAreaTop = this.getSafeAreaInset('top');
    const safeAreaBottom = this.getSafeAreaInset('bottom');

    const info: MobileDeviceInfo = {
      profile,
      screenHeight,
      screenWidth,
      isLandscape,
      safeAreaTop,
      safeAreaBottom,
    };

    console.log(`[XR] Mobile profile detected:`, info);
    return info;
  }

  /**
   * Get safe area inset value from CSS env()
   */
  private getSafeAreaInset(position: 'top' | 'bottom' | 'left' | 'right'): number {
    const testEl = document.createElement('div');
    testEl.style.cssText = `position: fixed; ${position}: 0; height: env(safe-area-inset-${position}, 0px); visibility: hidden;`;
    document.body.appendChild(testEl);
    const value = testEl.offsetHeight || 0;
    document.body.removeChild(testEl);
    return value;
  }

  /**
   * Calculate optimal Y position for mobile pane
   * Places pane in upper-middle area between screen top and control buttons
   */
  private calculateMobilePaneY(deviceInfo: MobileDeviceInfo): number {
    const baseEyeHeight = 1.6; // meters

    // Read offset tokens from CSS
    let yOffset: number;
    if (deviceInfo.isLandscape) {
      yOffset = getCSSNumber('--xr-mobile-pane-y-offset-landscape', 0.25);
    } else {
      switch (deviceInfo.profile) {
        case 'large':
          yOffset = getCSSNumber('--xr-mobile-pane-y-offset-large', 0.40);
          break;
        case 'standard':
          yOffset = getCSSNumber('--xr-mobile-pane-y-offset-standard', 0.35);
          break;
        case 'small':
        default:
          yOffset = getCSSNumber('--xr-mobile-pane-y-offset-small', 0.30);
          break;
      }
    }

    // Adjust for safe area (notch/Dynamic Island) - add a small boost if present
    if (deviceInfo.safeAreaTop > 40) {
      // Device has significant notch/island
      yOffset += 0.03; // Small additional offset
    }

    const finalY = baseEyeHeight + yOffset;
    console.log(`[XR] Mobile pane Y: ${finalY}m (base ${baseEyeHeight} + offset ${yOffset})`);
    return finalY;
  }

  /**
   * Disable page zoom for XR mode
   * Prevents pinch-to-zoom from zooming the page instead of controlling 3D camera
   */
  private disablePageZoom(): void {
    // Store original viewport content
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      this.originalViewportContent = viewport.getAttribute('content');
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    } else {
      // Create viewport meta if it doesn't exist
      const newViewport = document.createElement('meta');
      newViewport.name = 'viewport';
      newViewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.head.appendChild(newViewport);
      this.originalViewportContent = null;
    }

    // Store original body styles
    this.originalBodyStyles = {
      touchAction: document.body.style.touchAction,
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      width: document.body.style.width,
      height: document.body.style.height,
    };

    // Set XR active attribute (CSS handles the rest)
    document.body.setAttribute('data-xr-active', 'true');

    console.log('[XR] Page zoom disabled for XR mode');
  }

  /**
   * Restore page zoom after exiting XR mode
   */
  private restorePageZoom(): void {
    // Restore viewport
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      if (this.originalViewportContent !== null) {
        viewport.setAttribute('content', this.originalViewportContent);
      } else {
        // Remove the viewport we created
        viewport.remove();
      }
    }
    this.originalViewportContent = null;

    // Remove XR active attribute
    document.body.removeAttribute('data-xr-active');

    // Restore original body styles if we have them
    if (this.originalBodyStyles) {
      document.body.style.touchAction = this.originalBodyStyles.touchAction;
      document.body.style.overflow = this.originalBodyStyles.overflow;
      document.body.style.position = this.originalBodyStyles.position;
      document.body.style.width = this.originalBodyStyles.width;
      document.body.style.height = this.originalBodyStyles.height;
      this.originalBodyStyles = null;
    }

    console.log('[XR] Page zoom restored');
  }

  /**
   * Handle orientation change - reposition pane for new dimensions
   */
  private handleOrientationChange(): void {
    // Delay to allow dimensions to update
    setTimeout(() => {
      if (!this.readingPane || !this.isPreviewMode || !this.isGyroMode) return;

      // Recalculate device info
      this.mobileDeviceInfo = this.detectMobileProfile();

      // Calculate new Y position
      const mobileDistance = getCSSNumber('--xr-mobile-pane-distance-m', 0.8);
      const mobileY = this.calculateMobilePaneY(this.mobileDeviceInfo);

      // Reposition pane (maintain backward tilt)
      this.readingPane.position.set(0, mobileY, -mobileDistance);
      this.readingPane.rotation.x = 0.18; // ~10 degrees backward tilt
      console.log(`[XR] Pane repositioned after orientation change: Y=${mobileY}, Z=-${mobileDistance}`);
    }, 100);
  }

  /**
   * Start orientation change listener
   */
  private startOrientationChangeListener(): void {
    this.boundHandleOrientationChange = this.handleOrientationChange.bind(this);
    window.addEventListener('orientationchange', this.boundHandleOrientationChange);
    window.addEventListener('resize', this.boundHandleOrientationChange);
  }

  /**
   * Stop orientation change listener
   */
  private stopOrientationChangeListener(): void {
    if (this.boundHandleOrientationChange) {
      window.removeEventListener('orientationchange', this.boundHandleOrientationChange);
      window.removeEventListener('resize', this.boundHandleOrientationChange);
      this.boundHandleOrientationChange = null;
    }
  }

  /**
   * Handle window resize - update renderer and camera
   */
  private handleResize(): void {
    if (!this.renderer || !this.camera) return;

    const width = window.innerWidth;
    const height = window.innerHeight;

    // Update renderer size
    this.renderer.setSize(width, height);

    // Update camera aspect ratio and projection matrix
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    console.log(`[XR] Window resized: ${width}x${height}`);
  }

  /**
   * Start window resize listener
   */
  private startResizeListener(): void {
    this.boundHandleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.boundHandleResize);
  }

  /**
   * Stop window resize listener
   */
  private stopResizeListener(): void {
    if (this.boundHandleResize) {
      window.removeEventListener('resize', this.boundHandleResize);
      this.boundHandleResize = null;
    }
  }

  /**
   * Request device orientation permission (iOS 13+)
   */
  private async requestOrientationPermission(): Promise<boolean> {
    if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
      try {
        const permission = await (DeviceOrientationEvent as any).requestPermission();
        return permission === 'granted';
      } catch (err) {
        console.error('[XR] Failed to request orientation permission:', err);
        return false;
      }
    }
    return true; // Permission not required on this device
  }

  /**
   * Start gyro mode for mobile AR-like experience
   */
  private async startGyroMode(): Promise<boolean> {
    const hasPermission = await this.requestOrientationPermission();
    if (!hasPermission) {
      console.warn('[XR] Device orientation permission denied');
      return false;
    }

    this.boundHandleOrientation = this.handleDeviceOrientation.bind(this);
    window.addEventListener('deviceorientation', this.boundHandleOrientation, true);

    this.isGyroMode = true;
    this.initialOrientation = null; // Will be set on first orientation event
    console.log('[XR] Gyro mode started');
    return true;
  }

  /**
   * Stop gyro mode
   */
  private stopGyroMode(): void {
    if (this.boundHandleOrientation) {
      window.removeEventListener('deviceorientation', this.boundHandleOrientation, true);
      this.boundHandleOrientation = null;
    }
    this.isGyroMode = false;
    this.initialOrientation = null;
  }

  /**
   * Start camera passthrough for mobile AR mode
   * Shows device camera as background instead of 3D environment
   */
  private async startCameraPassthrough(_container: HTMLElement): Promise<boolean> {
    try {
      // Request camera access (rear-facing camera for AR)
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      this.cameraStream = stream;

      // Create video element to display camera feed
      // Append to body (not container) to avoid z-index stacking context issues
      const video = document.createElement('video');
      video.srcObject = stream;
      video.autoplay = true;
      video.playsInline = true;
      video.muted = true;
      video.setAttribute('playsinline', ''); // iOS requires this attribute
      video.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        object-fit: cover;
        z-index: 998;
        pointer-events: none;
      `;
      document.body.appendChild(video);
      this.cameraVideoElement = video;

      // Wait for video to start playing
      await video.play();
      console.log('[XR] Camera video playing');

      // Make Three.js background transparent so camera shows through
      if (this.scene) {
        this.scene.background = null;
        // Disable fog (it renders over transparent background)
        this.scene.fog = null;
      }
      if (this.renderer) {
        this.renderer.setClearColor(0x000000, 0);
      }

      // Hide environment group (grid, shapes)
      if (this.environmentGroup) {
        this.environmentGroup.visible = false;
      }

      // Hide hints pane in camera mode (keyboard shortcuts not useful on mobile)
      if (this.hintsPane) {
        this.hintsPane.visible = false;
      }

      // Save 3D mode pane state and apply optimal camera mode pane settings
      if (this.readingPane) {
        // Save current pane state from 3D mode
        this.saved3DPanePosition = this.readingPane.position.clone();
        this.saved3DPaneRotation = this.readingPane.rotation.clone();
        this.saved3DPaneScale = this.readingPane.scale.clone();

        // Apply optimal camera mode pane settings (decoupled from 3D mode)
        // Camera is at (0, 1.3, 0.2) with 8° down tilt, FOV 70°
        // Position pane to appear: parallel to screen, 1.5 pane heights from top, 75% screen width
        // At 0.8m distance with 70° FOV, 75% width ≈ scale 1.3
        // Position higher in view to be ~1.5 pane heights from top
        const cameraModeDistance = 0.8;
        const cameraModeScale = 1.56; // ~90% of screen width (20% larger than 1.3)
        // Place pane so its top is ~1.5 scaled heights below top of view
        // Camera looking slightly down, so pane Y above camera Y appears in upper screen
        const paneY = 1.90; // Positioned 15% higher than before
        const paneZ = 0.2 - cameraModeDistance; // 0.8m in front of camera at Z=0.2

        this.readingPane.position.set(0, paneY, paneZ);
        this.readingPane.rotation.set(0, 0, 0); // Parallel to screen (facing camera)
        this.readingPane.scale.set(cameraModeScale, cameraModeScale, cameraModeScale);

        // Make reading pane 92% opaque for camera mode (high contrast for readable text)
        const material = this.readingPane.material as THREE.MeshBasicMaterial;
        material.opacity = 0.92;
      }

      // Disable pane controls in camera mode (pane stays fixed in view)
      if (this.paneControls) {
        this.paneControls.disable();
      }

      // Enable scene controls for camera mode (like before 2pm)
      // This handles touch gestures and prevents browser zoom
      if (this.camera && this.renderer && !this.sceneControls) {
        this.sceneControls = createXRSceneControls(this.camera, this.renderer.domElement, {
          target: new THREE.Vector3(0, 1.6, -this.config.paneDistance),
        });
        this.sceneControls.enable();
        console.log('[XR] Scene controls enabled for camera mode');
      }

      // Save 3D mode camera settings and apply camera mode settings
      if (this.camera) {
        this.saved3DCameraPosition = this.camera.position.clone();
        this.saved3DCameraRotation = this.camera.rotation.clone();
        this.saved3DTiltDeg = this.mobileStartTiltDeg;

        // Apply the "perfect" camera mode settings
        this.camera.position.set(0, 1.3, 0.2);
        this.mobileStartTiltDeg = 8;

        // Apply the rotation directly (gyro is disabled in camera mode)
        this.camera.rotation.order = 'YXZ';
        this.camera.rotation.x = THREE.MathUtils.degToRad(8); // 8 degrees down
        this.camera.rotation.y = 0;
        this.camera.rotation.z = 0;
      }

      this.isCameraMode = true;
      console.log('[XR] Camera passthrough started');
      return true;
    } catch (error) {
      console.error('[XR] Failed to start camera passthrough:', error);
      return false;
    }
  }

  /**
   * Stop camera passthrough
   */
  private stopCameraPassthrough(): void {
    if (!this.isCameraMode) return;

    // Stop camera stream
    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach(track => track.stop());
      this.cameraStream = null;
    }

    // Remove video element
    if (this.cameraVideoElement) {
      if (this.cameraVideoElement.parentElement) {
        this.cameraVideoElement.parentElement.removeChild(this.cameraVideoElement);
      }
      this.cameraVideoElement = null;
    }

    // Restore environment visibility
    if (this.environmentGroup) {
      this.environmentGroup.visible = true;
    }

    // Restore scene background and fog
    if (this.scene) {
      this.scene.background = new THREE.Color(this.config.roomBg);
      this.scene.fog = new THREE.Fog(this.config.roomBg, 2, 15);
    }
    if (this.renderer) {
      this.renderer.setClearColor(new THREE.Color(this.config.clearColor), 1);
    }

    // Restore 3D mode pane state
    if (this.readingPane) {
      // Restore position, rotation, scale from 3D mode
      if (this.saved3DPanePosition) {
        this.readingPane.position.copy(this.saved3DPanePosition);
        this.saved3DPanePosition = null;
      }
      if (this.saved3DPaneRotation) {
        this.readingPane.rotation.copy(this.saved3DPaneRotation);
        this.saved3DPaneRotation = null;
      }
      if (this.saved3DPaneScale) {
        this.readingPane.scale.copy(this.saved3DPaneScale);
        this.saved3DPaneScale = null;
      }

      // Restore reading pane opacity
      const material = this.readingPane.material as THREE.MeshBasicMaterial;
      material.opacity = 0.95;
    }

    // Restore hints pane visibility
    if (this.hintsPane) {
      this.hintsPane.visible = true;
    }

    // Disable scene controls created for camera mode (mobile only)
    // In 3D mode, pane controls handle touch instead
    if (this.sceneControls && this.paneControls) {
      this.sceneControls.disable();
      this.sceneControls = null;
      console.log('[XR] Scene controls disabled, returning to 3D mode');
    }

    // Re-enable pane controls when returning to 3D mode
    if (this.paneControls) {
      this.paneControls.enable();
    }

    // Restore 3D mode camera settings
    if (this.camera && this.saved3DCameraPosition) {
      this.camera.position.copy(this.saved3DCameraPosition);
      if (this.saved3DCameraRotation) {
        this.camera.rotation.copy(this.saved3DCameraRotation);
        this.saved3DCameraRotation = null;
      }
      this.mobileStartTiltDeg = this.saved3DTiltDeg;
      this.saved3DCameraPosition = null;
    }

    this.isCameraMode = false;
    console.log('[XR] Camera passthrough stopped');
  }

  /**
   * Handle device orientation event
   */
  private handleDeviceOrientation(event: DeviceOrientationEvent): void {
    if (event.alpha === null || event.beta === null || event.gamma === null) return;

    // Capture initial orientation on first event
    if (!this.initialOrientation) {
      this.initialOrientation = {
        alpha: event.alpha,
        beta: event.beta,
        gamma: event.gamma,
      };
      console.log('[XR] Initial orientation captured:', this.initialOrientation);
    }

    // Store absolute orientation (not relative - for more responsive movement)
    this.deviceOrientation = {
      alpha: event.alpha,
      beta: event.beta,
      gamma: event.gamma,
    };
  }

  /**
   * Update camera rotation based on device orientation
   * Creates a "look through the phone" experience where turning the phone
   * reveals different parts of the 3D environment naturally.
   */
  private updateGyroRotation(): void {
    if (!this.camera || !this.isGyroMode) return;

    // Get device orientation values
    const alpha = this.deviceOrientation.alpha; // 0-360 compass heading
    const beta = this.deviceOrientation.beta;   // -180 to 180 front/back tilt
    // gamma (left/right tilt) not used - we don't want roll

    // Use initial orientation as reference point
    const alphaOffset = this.initialOrientation ? this.initialOrientation.alpha : 0;
    const betaOffset = this.initialOrientation ? this.initialOrientation.beta : 90;

    // Calculate relative rotation from initial orientation
    let relativeAlpha = alpha - alphaOffset;
    // Normalize to -180 to 180 range
    if (relativeAlpha > 180) relativeAlpha -= 360;
    if (relativeAlpha < -180) relativeAlpha += 360;

    // Calculate relative beta (tilt) from initial position
    const relativeBeta = beta - betaOffset;

    // Clamp vertical look to prevent flipping (limit to ~45 degrees up/down)
    // Tighter clamp prevents gimbal lock issues when phone tilts too far
    const clampedBetaRad = THREE.MathUtils.clamp(
      THREE.MathUtils.degToRad(relativeBeta),
      -Math.PI * 0.25,  // ~45 degrees down
      Math.PI * 0.25    // ~45 degrees up
    );

    // Convert horizontal rotation to radians
    const alphaRad = THREE.MathUtils.degToRad(relativeAlpha);

    // Target rotation values
    // Add mobile start tilt to look down at scene (positive X = look down)
    const startTiltRad = THREE.MathUtils.degToRad(this.mobileStartTiltDeg);
    const targetX = clampedBetaRad + startTiltRad;
    const targetY = alphaRad;  // Positive = phone right, look right

    // Smoothing factor (0 = no smoothing, 1 = instant)
    // Higher = more responsive, lower = smoother but laggier
    const smoothing = 0.35;

    // Apply rotation with smoothing to reduce jerkiness
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.x = THREE.MathUtils.lerp(this.camera.rotation.x, targetX, smoothing);
    this.camera.rotation.y = THREE.MathUtils.lerp(this.camera.rotation.y, targetY, smoothing);
    this.camera.rotation.z = 0; // Explicitly reset Z every frame to prevent gimbal lock drift
  }

  /**
   * Render frame
   */
  private render(frame?: XRFrame): void {
    if (!this.renderer || !this.scene || !this.camera) return;

    // Process XR input if we have a valid frame and session
    if (frame && this.xrSession && this.inputHandler) {
      this.inputHandler.processInput(this.xrSession, frame);
    }

    // Update based on mode
    if (this.isPreviewMode) {
      // Always update scene controls for touch-based pan/zoom/rotate
      // This calls lookAt() to keep camera pointed at pane
      if (this.sceneControls) {
        this.sceneControls.update();
      }

      // Gyro panning: rotate camera based on device orientation
      // Only in 3D environment mode (not camera mode) - camera mode keeps pane centered
      // Runs AFTER scene controls so gyro rotation overrides lookAt()
      if (this.isGyroMode && !this.isCameraMode) {
        this.updateGyroRotation();
      }
    }

    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Toggle camera passthrough mode on/off
   * Returns the new camera mode state
   */
  async toggleCameraMode(): Promise<boolean> {
    if (!this.isPreviewMode) {
      return false;
    }

    if (this.isCameraMode) {
      this.stopCameraPassthrough();
      return false;
    } else {
      const started = await this.startCameraPassthrough(document.body as HTMLElement);
      return started;
    }
  }

  /**
   * Check if camera mode is currently active
   */
  get cameraActive(): boolean {
    return this.isCameraMode;
  }

  /**
   * Check if preview mode is active (for showing camera toggle)
   */
  get previewActive(): boolean {
    return this.isPreviewMode;
  }


  /**
   * End XR session
   */
  async endSession(): Promise<void> {
    if (this.xrSession) {
      try {
        await this.xrSession.end();
      } catch {
        // Session may already be ended
      }
      this.xrSession = null;
    }

    this.cleanup();
    this.isActive = false;
  }

  /**
   * Clean up resources
   */
  private cleanup(): void {
    // Restore page zoom first
    this.restorePageZoom();

    // Stop orientation change listener
    this.stopOrientationChangeListener();

    // Stop resize listener
    this.stopResizeListener();

    // Stop camera passthrough first (before removing renderer)
    this.stopCameraPassthrough();

    // Stop render loop
    if (this.renderer) {
      this.renderer.setAnimationLoop(null);

      // Remove from DOM
      if (this.renderer.domElement.parentElement) {
        this.renderer.domElement.parentElement.removeChild(this.renderer.domElement);
      }

      this.renderer.dispose();
      this.renderer = null;
    }

    // Unsubscribe from core
    if (this.unsubscribeCore) {
      this.unsubscribeCore();
      this.unsubscribeCore = null;
    }

    // Stop input handler
    if (this.inputHandler) {
      this.inputHandler.stop();
      this.inputHandler = null;
    }

    // Disable scene controls
    if (this.sceneControls) {
      this.sceneControls.disable();
      this.sceneControls = null;
    }

    // Disable pane controls
    if (this.paneControls) {
      this.paneControls.disable();
      this.paneControls = null;
    }

    // Dispose Three.js objects
    if (this.textTexture) {
      this.textTexture.dispose();
      this.textTexture = null;
    }

    if (this.readingPane) {
      this.readingPane.geometry.dispose();
      (this.readingPane.material as THREE.Material).dispose();
      this.readingPane = null;
    }

    // Dispose hints pane
    if (this.hintsTexture) {
      this.hintsTexture.dispose();
      this.hintsTexture = null;
    }

    if (this.hintsPane) {
      this.hintsPane.geometry.dispose();
      (this.hintsPane.material as THREE.Material).dispose();
      this.hintsPane = null;
    }

    // Stop gyro mode
    this.stopGyroMode();

    // Clear environment group
    this.environmentGroup = null;

    // Clear mobile device info
    this.mobileDeviceInfo = null;

    this.scene = null;
    this.camera = null;
    this.textCanvas = null;
    this.hintsCanvas = null;
    this.currentWord = '';
    this.isPreviewMode = false;
    this.isGyroMode = false;
  }

  /**
   * Check if session is active
   */
  get active(): boolean {
    return this.isActive;
  }

  /**
   * Check if in preview mode
   */
  get inPreview(): boolean {
    return this.isPreviewMode;
  }
}

// Singleton instance
let xrManager: XRSessionManager | null = null;

/**
 * Get the XR session manager (creates if needed)
 */
export function getXRManager(): XRSessionManager {
  if (!xrManager) {
    xrManager = new XRSessionManager();
  }
  return xrManager;
}

/**
 * Check if WebXR is available
 */
export async function isXRAvailable(): Promise<boolean> {
  return XRSessionManager.isSupported();
}

/**
 * Create the "Enter XR" button
 * Button label is exactly "Enter XR" per Constitution Article XII
 */
export function createEnterXRButton(
  core: ReaderCore,
  container: HTMLElement
): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'enter-xr-btn';
  button.textContent = 'Enter XR'; // Exact label required
  button.title = 'Enter immersive reading mode';
  button.setAttribute('aria-label', 'Enter XR');
  // Always enable - use preview mode as fallback when WebXR unavailable
  isXRAvailable().then((available) => {
    console.log(`[XR] Button state update: webxr=${available}, preview fallback enabled`);
    button.disabled = false;
    if (!available) {
      button.title = 'WebXR not available - will use 3D preview mode';
    }
  });

  // Handle click
  button.addEventListener('click', async () => {
    const manager = getXRManager();

    if (manager.active) {
      // Exit current session or preview
      if (manager.inPreview) {
        manager.endPreview();
      } else {
        await manager.endSession();
      }
      button.textContent = 'Enter XR';
    } else {
      button.disabled = true;
      button.textContent = 'Starting...';

      // Try full WebXR first, fall back to preview mode
      const xrAvailable = await isXRAvailable();
      let success = false;

      if (xrAvailable) {
        success = await manager.startSession(core, container);
      } else {
        console.log('[XR] WebXR not available, starting preview mode');
        success = await manager.startPreview(core, container);
      }

      if (success) {
        button.textContent = 'Exit XR';
        button.disabled = false;
      } else {
        button.textContent = 'Enter XR';
        button.disabled = false;
      }
    }
  });

  return button;
}
